import { useEffect, useState } from "react";
import { useBreadcrumb } from "sj-ui-toolkit/src/atlas/hooks/useBreadcrumbs";
import { AdminAppRoutes } from "sj-ui-toolkit/src/constants/routes";
import { useToast } from "sj-ui-toolkit/src/hooks";
import PersonalizedLessonAssignmentsTable from "@/components/pla/PersonalizedLessonAssignmentsTable";
import type { ApprovedPlaAssignmentRow } from "@/components/pla/types";
import { loadApprovedPersonalizedLessonAssignments } from "@/api/personalizedLessonAssignments";
import { useAspenFeatures } from "@/hooks/useAspenFeatures";
import { Skeleton } from "primereact/skeleton";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const PersonalizedLessonAssignment = () => {
  useBreadcrumb({
    label: "Personalized Lesson Assignment",
    route: AdminAppRoutes.routes.assignments.routes.personalizedLessonAssignment.route,
    display: "page",
    resetTrail: true,
  });

  const { showToast } = useToast();
  const { features, isLoading: featuresLoading, currentTenantName } = useAspenFeatures();

  const [assignments, setAssignments] = useState<ApprovedPlaAssignmentRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  // Bumped by `reload` to re-run the fetch effect on demand (e.g. after archiving).
  const [reloadKey, setReloadKey] = useState(0);
  const reload = () => setReloadKey(key => key + 1);

  useEffect(() => {
    if (!currentTenantName || !features.adapt) {
      setIsLoading(false);
      return;
    }

    // `cancelled` is flipped by the cleanup when the tenant changes (or the
    // component unmounts), so a stale response can't overwrite newer data.
    let cancelled = false;
    setIsLoading(true);

    loadApprovedPersonalizedLessonAssignments(currentTenantName)
      .then(data => {
        if (!cancelled) setAssignments(data);
      })
      .catch(error => {
        if (cancelled) return;
        console.error("Error fetching lesson assignments:", error);
        setAssignments([]);
        showToast({
          severity: "error",
          detail:
            "Unable to fetch personalized lesson assignments. Please try refreshing the page.",
        });
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [currentTenantName, features.adapt, reloadKey]);

  if (featuresLoading) {
    return <Skeleton width="100%" height="20rem" />;
  }

  return (
    <>
      <h1 className="mb-4">Personalized Lesson Assignment</h1>
      {features.adapt && (
        <PersonalizedLessonAssignmentsTable
          assignments={assignments}
          isLoading={isLoading}
          onRefetch={reload}
        />
      )}
    </>
  );
};

export default PersonalizedLessonAssignment;
